/**
 * SelfHostPage.tsx — self-hosting guide for ɳSentry
 *
 * Purpose:    Step-by-step guide to self-hosting ɳSentry using the ɳSelf CLI
 * Inputs:     Static steps data
 * Outputs:    Installation guide with code blocks, requirements, and help links
 * Constraints: React Router Link for internal navigation
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'

const steps = [
  {
    n: '01',
    title: 'Install the ɳSelf CLI',
    code: 'brew install nself-org/tap/nself',
    note: 'macOS or Linux. Windows via WSL2.',
  },
  {
    n: '02',
    title: 'Create or open a ɳSelf project',
    code: 'nself init myproject\ncd myproject',
    note: 'ɳSentry installs on any existing ɳSelf instance too. Skip init if you already have one.',
  },
  {
    n: '03',
    title: 'Set your license key',
    code: 'nself license set YOUR_KEY_HERE',
    note: 'Get a key at nself.org/pricing. ɳSelf+ includes ɳSentry. nself-audit is always free.',
  },
  {
    n: '04',
    title: 'Install the ɳSentry baseline plugins',
    code: 'nself plugin install nself-uptime-monitor nself-status-page nself-incident-mgmt nself-alert-router nself-slo-tracker nself-synthetic-monitor nself-rum',
    note: '7 baseline plugins. Expansion plugins (errors, cron-monitor, oncall, crash, anomaly) are optional add-ons.',
  },
  {
    n: '05',
    title: 'Configure and build',
    code: "nself env set NSENTRY_DOMAIN=status.yourname.com\nnself build",
    note: "Generates docker-compose.yml and nginx config. Let's Encrypt SSL auto-provisioned.",
  },
  {
    n: '06',
    title: 'Start your instance',
    code: 'nself start',
    note: 'Postgres, Hasura, Auth, Nginx, and all ɳSentry plugins come up. Prometheus and Grafana start alongside for free infra monitoring.',
  },
  {
    n: '07',
    title: 'Add your first uptime check',
    code: 'nself nsentry uptime add --url https://api.yourapp.com --interval 60',
    note: 'Checks every 60 seconds. Opens an incident automatically if the check fails 3 times in a row.',
  },
]

export function SelfHostPage() {
  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <h1 className="mb-4 text-4xl font-bold sm:text-5xl">Self-host ɳSentry</h1>
        <p className="text-lg text-brand-muted">
          ɳSentry runs on your VPS using the ɳSelf CLI. Status pages, incident management,
          uptime monitoring, and SLO tracking up in under 15 minutes.
        </p>
      </section>

      {/* Requirements */}
      <section className="border-t border-brand-subtle bg-brand-surface/30 py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-6 text-xl font-bold">What you need</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                title: 'VPS',
                body: 'Any Linux VPS with 2 GB RAM minimum. Hetzner CX23 (~$4/mo) is plenty for small teams. CX32 for heavier RUM or synthetic workloads.',
              },
              {
                title: 'Domain',
                body: "A domain you control. ɳSelf sets up Let's Encrypt SSL automatically via Nginx.",
              },
              {
                title: 'ɳSentry Bundle',
                body: '$0.99/mo license key from nself.org. Or ɳSelf+ at $3.99/mo for all bundles. nself-audit is free in every tier.',
              },
            ].map((r) => (
              <div key={r.title} className="rounded-lg border border-brand-subtle bg-brand-bg p-5">
                <h3 className="mb-2 font-semibold text-brand-ink">{r.title}</h3>
                <p className="text-sm text-brand-muted">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-2xl font-bold">Setup steps</h2>
          <div className="space-y-10">
            {steps.map((s) => (
              <div key={s.n} className="grid gap-4 sm:grid-cols-[4rem_1fr]">
                <p className="font-mono text-xs font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-400 pt-1">
                  {s.n}
                </p>
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-brand-ink">{s.title}</h3>
                  <pre className="mb-2 overflow-x-auto rounded-lg border border-brand-subtle bg-brand-surface px-4 py-3 font-mono text-sm text-brand-secondary">
                    <code>{s.code}</code>
                  </pre>
                  <p className="text-sm text-brand-muted">{s.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help */}
      <section className="border-t border-brand-subtle bg-brand-surface/30 py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-2xl font-bold">Need help?</h2>
          <p className="mb-8 text-brand-muted">
            Full documentation is on docs.nself.org. Community support is available in chat.nself.org.
            ɳSelf+ subscribers get priority support.
          </p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <a
              href="https://docs.nself.org/plugins/nsentry/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-brand-card px-6 py-2.5 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2 hover:text-brand-ink transition-colors"
            >
              ɳSentry docs
            </a>
            <Link
              to="/invite"
              className="rounded-md bg-sky-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-sky-400 transition-colors"
            >
              Join the waitlist
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
